import { createContext, useContext, type ReactNode } from 'react';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createCloudClient, createFunctionsTransport, createSupabaseAuthPort, NetworkError, SessionCheckError, type CloudClient, type Transport } from '@estudio/cloud-client';
import { ApiError } from '@estudio/contracts';
import {
  createBrowserBridge,
  createIndexedDbDraftStore,
  createMemoryDraftStore,
  type AuthPort,
  type AuthSession,
  type DesktopBridge,
  type DesktopInjection,
  type DraftStore,
} from '@estudio/desktop-contract';
import type { AppConfig } from './config.ts';

/**
 * Servicios inyectados en la UI. La interfaz solo habla con estos puertos:
 * nunca con SQL, tablas, Tauri, FFmpeg ni Python directamente.
 */
export interface Services {
  mode: 'cloud' | 'demo';
  client: CloudClient;
  auth: AuthPort;
  bridge: DesktopBridge;
  drafts: DraftStore;
  supabase: SupabaseClient | null;
}

declare global {
  interface Window {
    /** El ejecutable (parte B) inyecta aquí sus adaptadores antes de cargar la interfaz. */
    __ESTUDIO_DESKTOP__?: DesktopInjection;
    /** Forma anterior (solo el puente); se mantiene por compatibilidad. */
    __ESTUDIO_DESKTOP_BRIDGE__?: DesktopBridge;
  }
}

export function createCloudServices(config: AppConfig): Services {
  const desktop = window.__ESTUDIO_DESKTOP__ ?? {};
  const supabase = createClient(config.supabaseUrl, config.publishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      ...(desktop.sessionStorage ? { storage: desktop.sessionStorage } : {}),
    },
  });
  const auth = createSupabaseAuthPort(supabase, desktop.emailLink);
  const recoveries = new Map<string, Promise<AuthSession | null>>();
  const transport: Transport = {
    async call(key, input, opts) {
      let session: AuthSession | null;
      try { session = await auth.getSession(); }
      catch (e) { if (e instanceof SessionCheckError && e.offline) throw new NetworkError(e); throw e; }
      const request = (bearer: string | null) => createFunctionsTransport({
        functionsUrl: config.functionsUrl, publishableKey: config.publishableKey,
        getBearer: async () => bearer,
      }).call(key, input, opts);
      try { return await request(session?.accessToken ?? null); }
      catch (e) {
        if (!(e instanceof ApiError) || e.code !== 'AUTH_REQUIRED' || !session || opts?.signal?.aborted) throw e;
        const recoveryKey = `${session.userId}:${session.accessToken}`;
        let recovery = recoveries.get(recoveryKey);
        if (!recovery) {
          recovery = auth.recoverSession(session);
          recoveries.set(recoveryKey, recovery);
          void recovery.finally(() => { if (recoveries.get(recoveryKey) === recovery) recoveries.delete(recoveryKey); }).catch(() => undefined);
        }
        let fresh: AuthSession | null;
        try { fresh = await recovery; }
        catch (error) { if (error instanceof SessionCheckError && error.offline) throw new NetworkError(error); throw error; }
        if (!fresh || fresh.userId !== session.userId || fresh.accessToken === session.accessToken || opts?.signal?.aborted) throw e;
        // Recovery updates the session for the next explicit attempt. Never
        // replay mutations, uploads or provider actions automatically.
        throw new ApiError({ code: 'AUTH_REQUIRED', retryable: false,
          message: 'Actualizamos tu sesión. La acción no se repitió automáticamente; revisa su estado y vuelve a intentarlo si sigue pendiente.',
        }, e.requestId);
      }
    },
  };
  let drafts: DraftStore;
  if (desktop.drafts) drafts = desktop.drafts;
  else {
    try {
      drafts = createIndexedDbDraftStore();
    } catch {
      drafts = createMemoryDraftStore();
    }
  }
  return {
    mode: 'cloud',
    client: createCloudClient(transport),
    auth,
    bridge: desktop.bridge ?? window.__ESTUDIO_DESKTOP_BRIDGE__ ?? createBrowserBridge(),
    drafts,
    supabase,
  };
}

const Ctx = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <Ctx.Provider value={services}>{children}</Ctx.Provider>;
}

export function useServices(): Services {
  const s = useContext(Ctx);
  if (!s) throw new Error('ServicesProvider ausente');
  return s;
}
